import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MailPlus, Copy, Check } from "lucide-react";

const ROLES = ["principal", "teacher", "student", "parent", "accountant", "hr"];

export default function InvitesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "teacher", name: "" });
  const [lastUrl, setLastUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const { data } = await api.get("/invites");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    try {
      const { data } = await api.post("/invites", form);
      setLastUrl(data.invite_url);
      toast.success("Invite created", { description: "MOCKED: link shown below (would normally be emailed via Resend)" });
      setForm({ email: "", role: "teacher", name: "" });
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copy = () => {
    navigator.clipboard.writeText(lastUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="space-y-6" data-testid="invites-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Admin</div>
          <h1 className="text-4xl font-bold tracking-tight">Invitations</h1>
          <p className="text-muted-foreground text-sm mt-1">Invite new staff, parents or students. Registration is invite-only.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-2xl gap-2" data-testid="invite-open"><MailPlus className="h-4 w-4" /> New invite</Button></DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Create invite</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-2xl" data-testid="invite-email" />
              <Input placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-2xl" />
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-2xl" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button onClick={send} className="rounded-2xl" data-testid="invite-send">Send invite</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {lastUrl && (
        <Card className="rounded-2xl border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex flex-col md:flex-row gap-3 items-start md:items-center">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-primary">MOCK invite URL</div>
              <div className="text-xs mt-1 truncate font-mono">{lastUrl}</div>
            </div>
            <Button size="sm" variant="outline" onClick={copy} className="rounded-xl gap-1">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader><CardTitle className="text-base">All invites</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border/60">
          {items.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No invites yet.</div>}
          {items.map((i) => (
            <div key={i.id} className="py-3 flex items-center gap-3" data-testid={`invite-row-${i.id}`}>
              <div className="flex-1">
                <div className="font-medium">{i.email}</div>
                <div className="text-xs text-muted-foreground">Token …{String(i.token).slice(-8)}</div>
              </div>
              <Badge variant="outline" className="rounded-full capitalize">{i.role}</Badge>
              <Badge className={`rounded-full ${i.used_at ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
                {i.used_at ? "Used" : "Pending"}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
